import { useFlags } from 'launchdarkly-react-client-sdk'
import Router, { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { useToggle, useWindowSize } from 'react-use'

import { faBolt } from '@fortawesome/pro-light-svg-icons/faBolt'
import Cookies from 'js-cookie'

import { ProjectRole } from '@infura/packages.api'
import { FEATURE_FLAGS } from '@infura/packages.common/constants'
import { Heading } from '@infura/packages.ui/Heading'
import { ItemContent, Nudge } from '@infura/packages.ui/Nudge'

import Content from 'components/Content'
import { CreateProjectModal } from 'components/dashboard/CreateProjectModal'
import { KeyServiceToolbar } from 'components/dashboard/KeyServiceToolbar'
import { AnalyticsOverview } from 'components/dashboard/analytics-overview/AnalyticsOverview'
import { HomepageSkeleton } from 'components/dashboard/key/KeySkeletons'
import KeysList from 'components/dashboard/key/KeysList/KeysList'
import { ProductUpdatesSection } from 'components/dashboard/projects-dashboard/ProductUpdatesSection'
import GettingStartedTour from 'components/gettingStartedTour'
import NewSurveyModal from 'components/onboardingSurvey/NewSurveyModal'
import { WELCOME_SESSION } from 'components/onboardingWelcome'
import { UpgradePlanModalsContainer } from 'components/upgradePlan/UpgradePlanModalsContainer'

import useOfferings from 'hooks/useOfferings'
import useSubscription from 'hooks/useSubscription'
import useUser from 'hooks/useUser'

import { LOGIN_METHOD_NAME } from 'lib/constants'

const NUDGE_SESSION = {
  name: 'nudge_onboarding',
  off: 'hidden',
  on: 'visible'
}

export default function DashboardHomepage() {
  const { offerings } = useOfferings()
  const { user } = useUser()
  const flags = useFlags()
  // calling this to ensure trigger migration of free active users to maxio if needed.
  useSubscription()

  const [isCreateModalVisible, toggleCreateModalOpen] = useToggle(false)
  const [isUpgradePlanModalVisible, toggleUpgradePlanModalVisible] = useToggle(false)
  const [isOnboardVisible, setOnboardVisible] = useState(true)
  const [isSurveyModalVisible, setSurveyModalVisible] = useState(false)
  const [role, setRole] = useState<ProjectRole | 'all-roles'>('all-roles')
  const [verifyEnabled, setVerifyEnabled] = useState(false)
  const isNewAPIKeyPolicyEnabled = useMemo(() => flags[FEATURE_FLAGS.NEW_API_KEY_POLICY_ENABLED], [flags])
  const { asPath } = useRouter()

  const { width } = useWindowSize()

  useEffect(() => {
    // 1854px = 1536 (max container width) + 318 (gf survey width)
    width > 1854 ? Cookies.set('survey_enabled', 'true') : Cookies.set('survey_enabled', 'false')
  }, [width])

  // Detects if a user needs to be redirected to onboarding
  useEffect(() => {
    if (
      user &&
      !user.customer_onboarding?.is_fully_onboarded &&
      localStorage.getItem(WELCOME_SESSION.name) !== WELCOME_SESSION.skip
    ) {
      Router.replace('/welcome', undefined, { shallow: true })
    }
  }, [user])

  useEffect(() => {
    const initPageView = async () => {
      window.analytics.page(asPath, {
        login_method: sessionStorage.getItem(LOGIN_METHOD_NAME),
        page_name: 'Login Successful',
        timestamp: Date.now(),
        user_experience: 'B'
      })
    }
    if (window?.analytics) initPageView()
  }, [asPath])

  const hideOnboardingNudge = () => {
    setOnboardVisible(false)
    localStorage.setItem(NUDGE_SESSION.name, NUDGE_SESSION.off)
  }

  const showOnboardingModal = () => {
    setSurveyModalVisible(true)
  }

  const renderNudgeOptions = useMemo(() => {
    const options: Array<ItemContent> = []
    if (isOnboardVisible && !user?.customer_onboarded) {
      options.push({
        buttonText: 'Get started',
        clickButton: showOnboardingModal,
        description:
          'Quickly tell us your role and how you use our product. This will help us provide you the best experience possible. It will take less than 1 minute.',
        dismissCallback: hideOnboardingNudge,
        icon: faBolt,
        id: '02',
        title: 'Optimize your experience with Metamask Developer'
      })
    }

    return options
  }, [isOnboardVisible, verifyEnabled, user?.customer_onboarded])

  useMemo(() => {
    if (user && user?.discord_username) {
      setVerifyEnabled(false)
    } else {
      setVerifyEnabled(true)
    }
  }, [user])

  useEffect(() => {
    const sessionVal = sessionStorage.getItem(NUDGE_SESSION.name)
    if (!sessionVal) {
      setOnboardVisible(true)
      sessionStorage.setItem(NUDGE_SESSION.name, NUDGE_SESSION.on)
    }
    if (sessionVal && sessionVal === NUDGE_SESSION.off) {
      setOnboardVisible(false)
    }
  }, [])

  if (!user)
    return (
      <Content>
        <HomepageSkeleton />
      </Content>
    )

  return (
    <>
      <CreateProjectModal isOpen={isCreateModalVisible} onClose={toggleCreateModalOpen} />
      {isNewAPIKeyPolicyEnabled ? (
        <UpgradePlanModalsContainer
          isUpgradePlanModalVisible={isUpgradePlanModalVisible}
          offerings={offerings}
          toggleUpgradePlanModalVisible={toggleUpgradePlanModalVisible}
        />
      ) : null}
      <Content>
        <div className='bg-background-alternative min-h-[calc(100vh_-_64px)]'>
          <div className='mx-auto w-full max-w-screen-2xl px-6 py-8'>
            {renderNudgeOptions.length > 0 ? (
              <div className='mb-4'>
                <Nudge options={[...renderNudgeOptions]} />
                <NewSurveyModal
                  hideOnboardingNudge={{ hideOnboardingNudge }}
                  isSurveyVisible={isSurveyModalVisible}
                  toggleSurveyVisible={() => setSurveyModalVisible(false)}
                  userId={user?.id}
                />
              </div>
            ) : null}
            <div className='m-auto mb-4 w-full items-end justify-between md:flex'>
              <Heading theme='page'>API Keys</Heading>
              {user ? (
                <KeyServiceToolbar
                  role={role}
                  setRole={setRole}
                  toggleCreateModalOpen={toggleCreateModalOpen}
                  toggleUpgradePlanModalOpen={toggleUpgradePlanModalVisible}
                />
              ) : null}
            </div>
            <KeysList role={role} />
            <GettingStartedTour />
            <AnalyticsOverview />
            <ProductUpdatesSection />
          </div>
        </div>
      </Content>
    </>
  )
}
